import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TuiAxes, TuiBar } from '@taiga-ui/addon-charts';
import { exist } from '@campaign-portal/namespace/common/id';
import { Campaign, CampaignStatus } from '@campaign-portal/namespace/entities/campaigns/specs';
import { calculateBarChartMax } from '@shared/utils';
import { ChannelType } from '@campaign-portal/namespace/common/enums';
import { AlarisConfigService, AlarisLanguageService, ifStateAnimation } from '@campaign-portal/components-library';
import { CompactNumberPipe } from '@shared/pipes';
import { CHART_HORIZONTAL_LINES_DEFAULT } from '@shared/constants';

const CHART_MIN_STEP_FOR_LABELS = 5;

@Component({
	selector: 'app-campaign-details-chart',
	standalone: true,
	animations: [ifStateAnimation],
	imports: [DecimalPipe, TranslateModule, TuiAxes, TuiBar],
	providers: [CompactNumberPipe],
	templateUrl: './campaign-details-chart.component.html',
	styleUrl: './campaign-details-chart.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignDetailsChartComponent implements OnChanges {
	@Input({ required: true }) campaign!: Campaign<exist>;

	isSeenExist = false;
	axisYLabels: string[] = [];
	axisXLabels: string[] = [];

	chartMax = 0;
	chartValue: number[][] = [];
	chartHasValues = false;
	readonly horizontalLines = CHART_HORIZONTAL_LINES_DEFAULT;
	readonly CampaignStatus = CampaignStatus;

	constructor(
		public readonly config: AlarisConfigService,
		private readonly compactNumber: CompactNumberPipe,
		private readonly langService: AlarisLanguageService
	) {}

	ngOnChanges(): void {
		this.chartValue = this.generateData();
		this.chartMax = calculateBarChartMax(this.chartValue);
		this.axisYLabels = this.generateAxisYLabels();
		this.axisXLabels = this.generateAxisXLabels();

		this.chartHasValues = this.chartValue.flat().some((amount) => amount > 0);
	}

	generateAxisXLabels(): string[] {
		const hasSeenChannel = this.campaign.setup.channels.some(
			(c) => c.channelType === ChannelType.VIBER || c.channelType === ChannelType.WHATSAPP
		);
		return hasSeenChannel
			? [
					this.langService.translate('enums.SENT'),
					this.langService.translate('enums.DELIVERED'),
					this.langService.translate('enums.SEEN'),
					this.langService.translate('enums.UNDELIVERED'),
					this.langService.translate('enums.FAILED'),
				]
			: [
					this.langService.translate('enums.SENT'),
					this.langService.translate('enums.DELIVERED'),
					this.langService.translate('enums.UNDELIVERED'),
					this.langService.translate('enums.FAILED'),
				];
	}

	generateAxisYLabels(): string[] {
		const step = this.chartMax / this.horizontalLines;
		if (step < CHART_MIN_STEP_FOR_LABELS) {
			return ['0', ...new Array(this.horizontalLines - 1).fill(''), this.chartMax.toFixed()];
		}

		const labels: string[] = [];
		for (let i = 0; i <= this.horizontalLines; i++) {
			labels.push(this.compactNumber.transform(i * step, 0));
		}
		return labels;
	}

	generateData(): number[][] {
		const hasSeenChannel = this.campaign.setup.channels.some(
			(c) => c.channelType === ChannelType.VIBER || c.channelType === ChannelType.WHATSAPP
		);

		const channelStats = this.campaign.statistics?.campaignChannelStatistics ?? [];
		const aggregated = channelStats.reduce(
			(acc, cs) => {
				acc.sent += cs.sent ?? 0;
				acc.delivered += cs.delivered ?? 0;
				acc.seen += cs.seen ?? 0;
				acc.undelivered += cs.undelivered ?? 0;
				acc.failed += cs.failed ?? 0;
				return acc;
			},
			{ sent: 0, delivered: 0, seen: 0, undelivered: 0, failed: 0 }
		);

		this.isSeenExist = hasSeenChannel;

		const statistics = hasSeenChannel
			? [aggregated.sent, aggregated.delivered, aggregated.seen, aggregated.undelivered, aggregated.failed]
			: [aggregated.sent, aggregated.delivered, aggregated.undelivered, aggregated.failed];

		return statistics.reduce((chartValue, amount, index) => {
			const singleBarValue: number[] = new Array(statistics.length).fill(0);
			singleBarValue[index] = amount ?? 0;
			chartValue.push(singleBarValue);

			return chartValue;
		}, [] as number[][]);
	}
}
